<template>
  <div class="pending-modal-backdrop" @click.self="handleBackdropClick">
    <div class="pending-modal-container" role="dialog" aria-modal="true" aria-labelledby="pending-title">
      <div class="pending-modal-header">
        <div class="header-text">
          <h2 id="pending-title" class="pending-modal-title">
            <i class="fas fa-envelope-open-text"/>
            Pending invitations
          </h2>
          <p class="pending-modal-subtitle">
            You have {{ items.length }}
            {{ items.length === 1 ? 'invitation' : 'invitations' }} waiting.
            Accept to join, or reject to clear.
          </p>
        </div>
        <button class="pending-modal-close" @click="handleClose" aria-label="Close">
          <i class="fas fa-times"/>
        </button>
      </div>

      <div class="pending-modal-body">
        <div v-if="items.length === 0" class="empty-state">
          <i class="fas fa-check-circle empty-icon"/>
          <p>You're all caught up.</p>
        </div>

        <ul v-else class="invite-list">
          <li
            v-for="item in items"
            :key="`${item.source}:${item.id}`"
            class="invite-row"
            :class="{ 'is-busy': busyIds.has(`${item.source}:${item.id}`) }"
          >
            <div class="invite-icon" aria-hidden="true">
              <i class="fas fa-building"/>
            </div>

            <div class="invite-body">
              <div class="invite-org">
                {{ item.organization?.name || 'Organization' }}
              </div>
              <div class="invite-meta">
                <span class="meta-pill">
                  <i class="fas fa-user-tag"/>
                  {{ formatRole(item.role) }}
                </span>
                <span v-if="item.invited_by?.email" class="meta-text">
                  invited by <strong>{{ item.invited_by.email }}</strong>
                </span>
                <span v-if="item.expires_at" class="meta-text muted">
                  expires {{ formatDate(item.expires_at) }}
                </span>
              </div>
              <div
                v-if="hasAllocation(item)"
                class="invite-allocation"
                :title="'Resources reserved for you in this organization'"
              >
                <i class="fas fa-microchip"/>
                <span>{{ formatAllocation(item.granted_resources) }}</span>
              </div>
            </div>

            <div class="invite-actions">
              <button
                type="button"
                class="btn btn-ghost"
                :disabled="busyIds.has(`${item.source}:${item.id}`)"
                @click="onReject(item)"
              >
                <i class="fas fa-times"/>
                Reject
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="busyIds.has(`${item.source}:${item.id}`)"
                @click="onAccept(item)"
              >
                <i v-if="busyIds.has(`${item.source}:${item.id}`)" class="fas fa-spinner fa-spin"/>
                <i v-else class="fas fa-check"/>
                Accept
              </button>
            </div>
          </li>
        </ul>
      </div>

      <div class="pending-modal-footer">
        <button type="button" class="btn btn-link" @click="handleClose">
          Decide later
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { authService } from '@/services/auth-api'

export default {
  name: 'PendingItemsModal',
  emits: ['close', 'accepted', 'rejected'],
  props: {
    initialItems: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      items: [...this.initialItems],
      // Tracks `${source}:${id}` keys currently in flight so we can disable
      // both buttons on a row mid-request without touching the others.
      busyIds: new Set()
    }
  },
  watch: {
    initialItems: {
      handler(next) {
        this.items = [...next]
      },
      deep: false
    }
  },
  methods: {
    ...mapActions(['showNotification']),

    keyFor(item) {
      return `${item.source}:${item.id}`
    },

    formatRole(role) {
      if (!role) return 'Member'
      const r = String(role)
      return r.charAt(0).toUpperCase() + r.slice(1).toLowerCase()
    },

    formatDate(value) {
      if (!value) return ''
      try {
        const d = new Date(value)
        if (Number.isNaN(d.getTime())) return value
        return d.toLocaleDateString(undefined, {
          year: 'numeric', month: 'short', day: 'numeric'
        })
      } catch {
        return value
      }
    },

    hasAllocation(item) {
      const g = item?.granted_resources
      return g && (g.cpu || g.ram_gb || g.storage_gb)
    },

    formatAllocation(g) {
      if (!g) return ''
      const parts = []
      if (g.cpu) parts.push(`${g.cpu} vCPU`)
      if (g.ram_gb) parts.push(`${g.ram_gb} GB RAM`)
      if (g.storage_gb) parts.push(`${g.storage_gb} GB storage`)
      return parts.join(' / ')
    },

    setBusy(key, busy) {
      // Set is reactive in Vue 3 only when reassigned, so spawn a fresh one.
      const next = new Set(this.busyIds)
      if (busy) next.add(key)
      else next.delete(key)
      this.busyIds = next
    },

    removeItem(item) {
      const key = this.keyFor(item)
      this.items = this.items.filter(i => this.keyFor(i) !== key)
      if (this.items.length === 0) {
        this.$emit('close')
      }
    },

    async onAccept(item) {
      const key = this.keyFor(item)
      if (this.busyIds.has(key)) return
      this.setBusy(key, true)
      try {
        await authService.acceptInvitation(item.id, item.source)
        this.showNotification({
          type: 'success',
          message: `You've joined ${item.organization?.name || 'the organization'}. ` +
                   `Switch to it from the dropdown when you're ready.`
        })
        this.$emit('accepted', item)
        this.removeItem(item)
      } catch (error) {
        console.error('Failed to accept invitation:', error)
        this.showNotification({
          type: 'error',
          message: error.response?.data?.message || error.message || 'Could not accept invitation.'
        })
      } finally {
        this.setBusy(key, false)
      }
    },

    async onReject(item) {
      const key = this.keyFor(item)
      if (this.busyIds.has(key)) return
      this.setBusy(key, true)
      try {
        await authService.rejectInvitation(item.id, item.source)
        this.showNotification({
          type: 'info',
          message: `Invitation to ${item.organization?.name || 'organization'} dismissed.`
        })
        this.$emit('rejected', item)
        this.removeItem(item)
      } catch (error) {
        console.error('Failed to reject invitation:', error)
        this.showNotification({
          type: 'error',
          message: error.response?.data?.message || error.message || 'Could not reject invitation.'
        })
      } finally {
        this.setBusy(key, false)
      }
    },

    handleBackdropClick() {
      // Backdrop click defers the decision but does not delete the items.
      this.handleClose()
    },

    handleClose() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.pending-modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(10, 37, 64, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 24px;
  animation: pi-fade-in 0.25s ease;
}

.pending-modal-container {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(10, 37, 64, 0.25);
  width: 100%;
  max-width: 640px;
  max-height: 88vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: pi-slide-up 0.3s ease;
}

.pending-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 28px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%);
}

.header-text {
  flex: 1 1 auto;
  min-width: 0;
}

.pending-modal-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #0a2540;
  margin: 0 0 6px;
  display: flex;
  align-items: center;
  gap: 12px;
  letter-spacing: -0.01em;
}

.pending-modal-title i {
  font-size: 1.1rem;
  color: #1a7fd4;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(26, 127, 212, 0.12);
  border-radius: 10px;
}

.pending-modal-subtitle {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.45;
}

.pending-modal-close {
  background: none;
  border: none;
  font-size: 1.1rem;
  color: #6b7280;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 8px;
  transition: all 0.2s;
  flex-shrink: 0;
}

.pending-modal-close:hover {
  background: rgba(10, 37, 64, 0.06);
  color: #0a2540;
}

.pending-modal-body {
  padding: 16px 24px 8px;
  overflow-y: auto;
  flex: 1 1 auto;
}

.empty-state {
  text-align: center;
  padding: 32px 16px;
  color: #6b7280;
}

.empty-icon {
  font-size: 2rem;
  color: #10b981;
  margin-bottom: 12px;
}

.invite-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.invite-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.2s ease;
}

.invite-row:hover {
  border-color: #c7d2fe;
  box-shadow: 0 4px 14px rgba(10, 37, 64, 0.06);
}

.invite-row.is-busy {
  opacity: 0.7;
}

.invite-icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(26, 127, 212, 0.08);
  color: #1a7fd4;
  border-radius: 10px;
  font-size: 1rem;
}

.invite-body {
  flex: 1 1 auto;
  min-width: 0;
}

.invite-org {
  font-weight: 600;
  font-size: 1.02rem;
  color: #0a2540;
  margin-bottom: 6px;
  word-break: break-word;
}

.invite-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.4;
}

.meta-text strong {
  color: #374151;
  font-weight: 600;
}

.meta-text.muted {
  color: #9ca3af;
}

.meta-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  background: #e0f2fe;
  color: #075985;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.meta-pill i {
  font-size: 0.7rem;
}

.invite-allocation {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #475569;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  padding: 4px 10px;
  border-radius: 8px;
}

.invite-allocation i {
  color: #1a7fd4;
}

.invite-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.pending-modal-footer {
  padding: 14px 24px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  background: #fafbfc;
}

.btn {
  padding: 9px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #1a7fd4;
  color: #ffffff;
}

.btn-primary:hover:not(:disabled) {
  background: #0d6ebd;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(26, 127, 212, 0.25);
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.btn-ghost {
  background: transparent;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-ghost:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #0a2540;
}

.btn-ghost:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-link {
  background: transparent;
  color: #6b7280;
}

.btn-link:hover {
  color: #1a7fd4;
  text-decoration: underline;
}

@keyframes pi-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes pi-slide-up {
  from { transform: translateY(16px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@media (max-width: 640px) {
  .pending-modal-container {
    max-width: 100%;
    border-radius: 14px;
  }

  .pending-modal-header,
  .pending-modal-footer {
    padding-left: 18px;
    padding-right: 18px;
  }

  .pending-modal-body {
    padding-left: 14px;
    padding-right: 14px;
  }

  .invite-row {
    flex-direction: column;
    gap: 12px;
  }

  .invite-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .invite-actions .btn {
    flex: 1 1 0;
    justify-content: center;
  }
}
</style>
